<template>
  <div class="section-content l">
    <div class="section-header section-header-alt">
      <div class="section-title">READINGS :</div>
      <div class="action-buttons" v-if="readings.length > 0">
        <button
            class="action-button"
            :disabled="exportingCSV"
            @click="exportCSV"
        >
          <svg v-if="!exportingCSV"
               xmlns="http://www.w3.org/2000/svg" width="14" height="14"
               viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          {{ exportingCSV ? 'Exporting…' : 'Export CSV' }}
        </button>
        <button class="action-button" @click="openDetailedReadings">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
            <circle cx="12" cy="12" r="3"></circle>
          </svg>
          View All
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <span>Loading readings data...</span>
    </div>
    <div v-else-if="error" class="error-state">
      <div class="error-message">{{ error }}</div>
      <button class="retry-button" @click="fetchReadings">Retry</button>
    </div>
    <div v-else-if="!deviceId" class="no-readings-message">
      <div class="info-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <p>No device attached to this metering point.</p>
      <p class="secondary-text">Readings will appear once a device is attached.</p>
    </div>
    <div v-else-if="readings.length > 0" class="readings-table">
      <div class="table-header">
        <div class="table-cell">VALUE</div>
        <div class="table-cell">DATE</div>
        <div class="table-cell">DIAGNOSTICS</div>
        <div class="table-cell">READED BY</div>
        <div class="table-cell">SESSION</div>
      </div>
      <div v-for="(reading, index) in readings" :key="index" class="table-row">
        <div class="table-cell">
          {{ formatReadingValue(reading) }}
        </div>
        <div class="table-cell">
          {{ formatDateTime(reading.date_current_reading) || 'N/A' }}
        </div>
        <div class="table-cell" :class="{'has-alarms': reading.current_diagnostic}">
          {{ reading.current_diagnostic ? 'WITH ALARMS' : 'NO ALARMS' }}
        </div>
        <div class="table-cell">
          {{ reading.readed_by || 'SYSTEM' }}
        </div>
        <div class="table-cell">
          {{ reading.dlms_session_id ? '...' : '' }}
        </div>
      </div>
      <div class="pagination-controls">
        <div class="pagination-info">
          Showing {{ startItem }}-{{ endItem }} of {{ totalItems }} items (Page {{ currentPage }} of {{ totalPages }})
        </div>
        <div class="pagination-buttons">
          <button
              class="pagination-button"
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
          >
            Previous
          </button>
          <div class="page-numbers">
            <button
                v-for="page in visiblePageNumbers"
                :key="page"
                :class="['page-number', { active: currentPage === page }]"
                @click="changePage(page)"
            >
              {{ page }}
            </button>
          </div>
          <button
              class="pagination-button"
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </div>
    <div v-else class="no-readings-message">
      <div class="info-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>
      <p>No readings available for this device.</p>
      <p class="secondary-text">Readings will appear once the device starts sending data.</p>
    </div>
  </div>
</template>

<script>
import { amrRawToM3, formatM3 } from '@/services/loraWbusHelper';

export default {
  name: 'MpidDetailsReadings',
  props: {
    deviceId: {
      type: String,
      default: ''
    },
    deviceProtocol: {
      type: String,
      default: ''
    },
    deviceSystemTitle: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      readings: [],
      loading: false,
      error: '',
      exportingCSV: false,
      currentPage: 1,
      perPage: 5,
      totalItems: 0,
      showReadingsOverlay: false
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.perPage));
    },
    startItem() {
      return this.totalItems === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
    },
    endItem() {
      return Math.min(this.startItem + this.readings.length - 1, this.totalItems);
    },
    visiblePageNumbers() {
      const maxVisible = 5;
      const half = Math.floor(maxVisible / 2);
      let start = Math.max(1, this.currentPage - half);
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      if (end === this.totalPages) {
        start = Math.max(1, end - maxVisible + 1);
      }
      const pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  watch: {
    deviceId: {
      immediate: true,
      handler(newDeviceId) {
        if (newDeviceId) {
          this.fetchReadings();
        } else {
          this.readings = [];
          this.totalItems = 0;
        }
      }
    }
  },
  methods: {
    formatReadingValue(reading) {
      const additionalData = reading?.additional_data || {};
      const rawValue = additionalData.Volume?.Raw ?? reading?.current_reading;
      const converted = amrRawToM3(rawValue, additionalData);

      if (converted !== null) return formatM3(converted);

      return reading?.current_reading ?? 'N/A';
    },

    async fetchReadings() {
      if (!this.deviceId) return;

      this.loading = true;
      this.error = '';
      const skip = (this.currentPage - 1) * this.perPage;
      const apiBaseUrl = process.env.VUE_APP_API_URL;
      try {
        const response = await fetch(
            `${apiBaseUrl}/readings/device/${this.deviceId}?limit=${this.perPage}&skip=${skip}`,
            {
              method: 'GET',
              headers: {
                'accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token')}`
              }
            }
        );
        if (!response.ok) {
          throw new Error(`Error fetching readings: ${response.statusText}`);
        }
        const data = await response.json();
        this.readings = data.readings || [];
        this.totalItems = data.count || 0;
      } catch (err) {
        console.error(err);
        this.error = err.message || 'Failed to load readings';
      } finally {
        this.loading = false;
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchReadings();
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
      } catch (error) {
        console.error('Error formatting date time:', error);
        return 'Error';
      }
    },
    async exportCSV() {
      const protocol = this.deviceProtocol ? this.deviceProtocol.toLowerCase() : '';

      // Standard readings endpoint covers LoRa, WBUS and combined LoRa/WBUS devices.
      const isStandardReadingDevice = protocol === 'lora' || protocol === 'wbus' || protocol === 'lora_wbus';

      // Determine if this is a DLMS device
      const isDLMSDevice = protocol === 'dlms';

      // For LoRa/WBUS devices, use general readings endpoint
      if (isStandardReadingDevice) {
        if (!this.deviceId) {
          alert('Device ID missing — cannot export.');
          return;
        }

        this.exportingCSV = true;
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const endpoint = `${apiBaseUrl}/readings/device/${encodeURIComponent(this.deviceId)}/download`;

        try {
          const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              accept: 'text/csv',
              Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
            },
          });

          if (!response.ok) {
            throw new Error(`Export failed (${response.status})`);
          }

          const csvBlob = await response.blob();
          const url = window.URL.createObjectURL(csvBlob);
          const link = document.createElement('a');
          const today = new Date().toISOString().slice(0, 10); // yyyy-mm-dd
          link.href = url;
          link.download = `readings_${this.deviceId}_${today}.csv`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (err) {
          console.error(err);
          alert(err.message || 'Unknown error while exporting CSV');
        } finally {
          this.exportingCSV = false;
        }
      }
      // For DLMS devices, use DLMS readings endpoint
      else if (isDLMSDevice) {
        if (!this.deviceSystemTitle) {
          alert('System Title missing — cannot export.');
          return;
        }

        this.exportingCSV = true;
        const apiBaseUrl = process.env.VUE_APP_API_URL;
        const endpoint = `${apiBaseUrl}/dlms/readings/systemtitle/${encodeURIComponent(this.deviceSystemTitle)}/export/csv`;

        try {
          const response = await fetch(endpoint, {
            method: 'GET',
            headers: {
              accept: 'text/csv',
              Authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
            },
          });

          if (!response.ok) {
            throw new Error(`Export failed (${response.status})`);
          }

          const csvBlob = await response.blob();
          const url = window.URL.createObjectURL(csvBlob);
          const link = document.createElement('a');
          const today = new Date().toISOString().slice(0, 10); // yyyy-mm-dd
          link.href = url;
          link.download = `dlms_readings_${this.deviceSystemTitle}_${today}.csv`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (err) {
          console.error(err);
          alert(err.message || 'Unknown error while exporting CSV');
        } finally {
          this.exportingCSV = false;
        }
      }
      // Unsupported protocol
      else {
        alert(`CSV export not supported for protocol: ${this.deviceProtocol || 'Unknown'}`);
      }
    },
    openDetailedReadings() {
      this.showReadingsOverlay = true;
    }
  }
};
</script>

<style scoped>
.section-content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  display: inline-block;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
}

.action-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.no-readings-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  opacity: 0.8;
  text-align: center;
}

.info-icon {
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 16px;
}

.no-readings-message p {
  font-size: 13px;
  max-width: 80%;
  margin-bottom: 8px;
}

.secondary-text {
  font-size: 11px !important;
  opacity: 0.7;
}

.readings-table {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  flex-grow: 1;
}

.table-header {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.table-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  padding: 15px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 11px;
  color: #CFCFCF;
}

.table-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 10px;
}

.has-alarms {
  color: #ff8a80;
}

.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 16px 0;
}

.pagination-info {
  font-size: 11px;
  opacity: 0.7;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-button {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
  opacity: 1;
}

.pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number {
  background: none;
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
}

.l {
  padding-left: 30px;
}


.page-number:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.1);
}

.page-number.active {
  background-color: rgba(255, 255, 255, 0.2);
  font-weight: 600;
}

@media (max-width: 767.98px) {
  .section-header {
    margin-top: 60px;
  }

  .table-header, .table-row {
    grid-template-columns: repeat(3, 1fr);
  }

  .table-cell:nth-child(4),
  .table-cell:nth-child(5) {
    display: none;
  }

  .pagination-controls {
    flex-direction: column;
    gap: 16px;
  }
}
</style>
